import React, { useState, useCallback, useEffect, useMemo } from 'react';
import {
  MapContainer,
  TileLayer,
  Circle,
  CircleMarker,
  Popup,
  Polyline,
  useMap,
} from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import {
  getMovementSummary,
  getMovementStateConfig,
  getHeadingConfig,
  formatSpeed,
  formatDistance,
  formatDuration as formatMovementDuration,
  getSpeedColor,
} from '../../../services/movementAnalytics.service';
import {
  getRouteHistory,
  formatRouteTime,
  formatRouteDateTime,
  getHeadingDisplay,
  getStopConfig,
  getRouteSegmentColor,
  interpolatePosition,
  calculateRouteBounds,
} from '../../../services/routeReconstruction.service';
import { PatternClusteringPanel } from '../PatternClustering';
import { BehavioralHeatmap, TemporalHeatmap } from '../Heatmaps';
import { FeatureInfoButton } from '../FeatureDocumentation';
import AddressDisplay, { AddressFromMap } from '../../AddressDisplay';
import { useAddressMap } from '../../../hooks/useReverseGeocode';

// Fix Leaflet icon paths
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl:
    'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl:
    'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl:
    'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

const PolActivityTab = ({ personId }) => {
  // --------------- Movement Analytics State ---------------
  const [movementSummary, setMovementSummary] = useState(null);
  const [isLoadingMovement, setIsLoadingMovement] = useState(false);
  const [movementHours, setMovementHours] = useState('24');

  // --------------- Route Reconstruction State ---------------
  const [routeData, setRouteData] = useState(null);
  const [isLoadingRoute, setIsLoadingRoute] = useState(false);
  const [routeHours, setRouteHours] = useState('24');
  const [routeSimplify, setRouteSimplify] = useState(true);
  const [playbackProgress, setPlaybackProgress] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [playbackSpeed, setPlaybackSpeed] = useState(1);

  // --------------- Local Error State ---------------
  const [error, setError] = useState(null);

  // --------------- Address Resolution ---------------
  const stopCoords = useMemo(() => {
    if (!routeData?.stops) return [];
    return routeData.stops.map((s) => ({ lat: s.latitude, lon: s.longitude }));
  }, [routeData?.stops]);
  const { addressMap: stopAddressMap } = useAddressMap(stopCoords);

  // ==================== Handlers ====================

  const handleGetMovementSummary = useCallback(async () => {
    if (!personId) {
      setError('Please enter a Person ID');
      return;
    }

    setIsLoadingMovement(true);
    setError(null);
    setMovementSummary(null);

    try {
      const hours = parseInt(movementHours, 10) || 24;
      const result = await getMovementSummary(parseInt(personId, 10), hours);
      if (result) {
        setMovementSummary(result);
      } else {
        setError('No movement data found.');
      }
    } catch (err) {
      setError('Failed to get movement summary: ' + (err.message || 'Unknown error'));
      console.error(err);
    } finally {
      setIsLoadingMovement(false);
    }
  }, [personId, movementHours]);

  const handleGetRouteHistory = useCallback(async () => {
    if (!personId) {
      setError('Please enter a Person ID');
      return;
    }

    setIsLoadingRoute(true);
    setError(null);
    setRouteData(null);
    setPlaybackProgress(0);
    setIsPlaying(false);

    try {
      const hours = parseInt(routeHours, 10) || 24;
      const result = await getRouteHistory(parseInt(personId, 10), hours, routeSimplify);
      if (result) {
        setRouteData(result);
      } else {
        setError('Failed to get route history. No GPS data found.');
      }
    } catch (err) {
      setError('Failed to reconstruct route: ' + (err.message || 'Unknown error'));
      console.error(err);
    } finally {
      setIsLoadingRoute(false);
    }
  }, [personId, routeHours, routeSimplify]);

  const handlePlayPause = useCallback(() => {
    setIsPlaying(prev => !prev);
  }, []);

  const handlePlaybackSpeedChange = useCallback((speed) => {
    setPlaybackSpeed(speed);
  }, []);

  const handleProgressChange = useCallback((e) => {
    const progress = parseFloat(e.target.value);
    setPlaybackProgress(progress);
  }, []);

  // Playback animation effect
  useEffect(() => {
    if (!isPlaying || !routeData || !routeData.points || routeData.points.length === 0) {
      return;
    }

    const interval = setInterval(() => {
      setPlaybackProgress(prev => {
        const increment = 0.005 * playbackSpeed;
        if (prev >= 1) {
          setIsPlaying(false);
          return 1;
        }
        return Math.min(prev + increment, 1);
      });
    }, 50);

    return () => clearInterval(interval);
  }, [isPlaying, playbackSpeed, routeData]);

  return (
    <div className="space-y-8">
      {/* Error Banner */}
      {error && (
        <div className="bg-red-50 border border-red-300 text-red-800 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {/* ==================== SECTION 1: CURRENT MOVEMENT ==================== */}
      <section id="current-movement">
        <div className="bg-white rounded-lg shadow p-4">
          <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
            Current Movement
            <span className="text-xs bg-sky-100 text-sky-700 px-2 py-1 rounded">Real-Time Speed & Heading</span>
            <FeatureInfoButton feature="movement" label="Docs" />
          </h3>
          <p className="text-gray-600 text-sm mb-4">
            Real-time analysis of movement patterns including current speed, heading, movement state,
            and daily travel statistics. Detects stationary periods and alerts on velocity threshold violations.
          </p>

          <div className="flex gap-4 mb-4 items-end">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Analysis Period (hours)</label>
              <input
                type="text"
                value={movementHours}
                onChange={(e) => setMovementHours(e.target.value)}
                className="w-24 p-2 border rounded-md"
                placeholder="24"
              />
            </div>
            <button
              onClick={handleGetMovementSummary}
              disabled={!personId || isLoadingMovement}
              className="px-4 py-2 bg-sky-600 text-white rounded hover:bg-sky-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              {isLoadingMovement ? 'Loading...' : 'Get Movement Summary'}
            </button>
          </div>

          {movementSummary && (
            <div className="space-y-4">
              {/* Current State Cards */}
              <div className="grid grid-cols-4 gap-4">
                {/* Movement State */}
                <div className="rounded-lg p-4 text-center" style={{ backgroundColor: getMovementStateConfig(movementSummary.movement_state).bgColor }}>
                  <div className="text-3xl mb-1">{getMovementStateConfig(movementSummary.movement_state).icon}</div>
                  <div className="font-semibold" style={{ color: getMovementStateConfig(movementSummary.movement_state).color }}>
                    {getMovementStateConfig(movementSummary.movement_state).label}
                  </div>
                  <div className="text-xs text-gray-500 mt-1">Current State</div>
                </div>

                {/* Current Speed */}
                <div className="bg-gray-50 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold" style={{ color: getSpeedColor(movementSummary.current_speed_mph) }}>
                    {formatSpeed(movementSummary.current_speed_mph)}
                  </div>
                  <div className="text-xs text-gray-500 mt-1">Current Speed</div>
                  {movementSummary.is_stationary && (
                    <div className="text-xs text-gray-600 mt-1">
                      Stationary for {formatMovementDuration(movementSummary.stationary_duration_mins)}
                    </div>
                  )}
                </div>

                {/* Heading */}
                <div className="bg-gray-50 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-gray-700">
                    {movementSummary.current_heading_degrees != null ? (
                      <>
                        <span className="text-3xl">{getHeadingConfig(movementSummary.heading_direction).arrow}</span>
                        <span className="text-lg ml-1">{movementSummary.heading_direction}</span>
                      </>
                    ) : (
                      <span className="text-gray-400">--</span>
                    )}
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    {movementSummary.current_heading_degrees != null
                      ? `${movementSummary.current_heading_degrees.toFixed(0)}\u00B0`
                      : 'No heading data'}
                  </div>
                </div>

                {/* Distance Today */}
                <div className="bg-sky-50 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-sky-600">
                    {formatDistance(movementSummary.distance_traveled_today_miles)}
                  </div>
                  <div className="text-xs text-gray-500 mt-1">Distance Today</div>
                </div>
              </div>

              {/* Daily Stats */}
              <div className="grid grid-cols-3 gap-4">
                <div className="border rounded-lg p-3">
                  <div className="text-sm text-gray-500">Average Speed</div>
                  <div className="text-xl font-semibold">{formatSpeed(movementSummary.avg_speed_today_mph)}</div>
                </div>
                <div className="border rounded-lg p-3">
                  <div className="text-sm text-gray-500">Max Speed</div>
                  <div className="text-xl font-semibold" style={{ color: getSpeedColor(movementSummary.max_speed_today_mph) }}>
                    {formatSpeed(movementSummary.max_speed_today_mph)}
                  </div>
                </div>
                <div className="border rounded-lg p-3">
                  <div className="text-sm text-gray-500">Points Analyzed</div>
                  <div className="text-xl font-semibold">{movementSummary.points_analyzed}</div>
                </div>
              </div>

              {/* Last Known Location */}
              {movementSummary.last_known_location && (
                <div className="border rounded-lg p-3">
                  <div className="text-sm text-gray-500 mb-1">Last Known Location</div>
                  <div className="flex justify-between items-center">
                    <div className="text-sm">
                      <AddressDisplay
                        lat={movementSummary.last_known_location.latitude}
                        lon={movementSummary.last_known_location.longitude}
                        className="font-medium"
                      />
                    </div>
                    <div className="text-xs text-gray-500">
                      {new Date(movementSummary.last_known_location.timestamp).toLocaleString()}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Movement Analytics Reference */}
        <details className="mt-4">
          <summary className="cursor-pointer text-sm text-sky-700 font-medium hover:text-sky-900">
            Movement Analytics Reference
          </summary>
          <div className="bg-sky-50 rounded-lg p-4 border border-sky-200 mt-2">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <h5 className="font-medium text-sky-700 mb-2">Movement States</h5>
                <ul className="space-y-1 text-gray-700">
                  <li><span className="text-lg">{'\u23F8'}</span> <strong>Stationary:</strong> {'<'} 0.5 mph</li>
                  <li><span className="text-lg">{'\uD83D\uDEB6'}</span> <strong>Walking:</strong> 0.5 - 4 mph</li>
                  <li><span className="text-lg">{'\uD83C\uDFC3'}</span> <strong>Running:</strong> 4 - 8 mph</li>
                  <li><span className="text-lg">{'\uD83D\uDE97'}</span> <strong>Driving:</strong> 8 - 65 mph</li>
                  <li><span className="text-lg">{'\uD83D\uDE98'}</span> <strong>Fast Driving:</strong> 65 - 85 mph</li>
                  <li><span className="text-lg">{'\u26A0'}</span> <strong>Suspicious:</strong> 85 - 120 mph</li>
                  <li><span className="text-lg">{'\u274C'}</span> <strong>Impossible:</strong> {'>'} 500 mph (spoofing)</li>
                </ul>
              </div>
              <div>
                <h5 className="font-medium text-sky-700 mb-2">Heading Directions</h5>
                <div className="grid grid-cols-4 gap-2 text-center text-gray-700">
                  <div><span className="text-xl">{'\u2191'}</span><br/>N</div>
                  <div><span className="text-xl">{'\u2197'}</span><br/>NE</div>
                  <div><span className="text-xl">{'\u2192'}</span><br/>E</div>
                  <div><span className="text-xl">{'\u2198'}</span><br/>SE</div>
                  <div><span className="text-xl">{'\u2193'}</span><br/>S</div>
                  <div><span className="text-xl">{'\u2199'}</span><br/>SW</div>
                  <div><span className="text-xl">{'\u2190'}</span><br/>W</div>
                  <div><span className="text-xl">{'\u2196'}</span><br/>NW</div>
                </div>
              </div>
            </div>
          </div>
        </details>
      </section>

      {/* ==================== SECTION 2: TRAVEL HISTORY ==================== */}
      <section id="travel-history">
        <div className="bg-white rounded-lg shadow p-4">
          <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
            Travel History
            <span className="text-xs bg-violet-100 text-violet-700 px-2 py-1 rounded">Historical Path Visualization</span>
            <FeatureInfoButton feature="route" label="Docs" />
          </h3>
          <p className="text-gray-600 text-sm mb-4">
            Reconstruct historical movement routes from GPS data. Visualize the path with animated playback,
            identify stops, and review travel statistics over configurable time periods.
          </p>

          {/* Controls */}
          <div className="flex flex-wrap gap-4 items-end">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Hours of History</label>
              <input
                type="number"
                value={routeHours}
                onChange={(e) => setRouteHours(e.target.value)}
                className="w-24 px-3 py-2 border border-gray-300 rounded-md"
                min="1"
                max="168"
              />
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="routeSimplify"
                checked={routeSimplify}
                onChange={(e) => setRouteSimplify(e.target.checked)}
                className="rounded border-gray-300"
              />
              <label htmlFor="routeSimplify" className="text-sm text-gray-700">Simplify Route</label>
            </div>
            <button
              onClick={handleGetRouteHistory}
              disabled={isLoadingRoute || !personId}
              className="px-4 py-2 bg-violet-600 text-white rounded-md hover:bg-violet-700 disabled:opacity-50"
            >
              {isLoadingRoute ? 'Loading...' : 'Get Route History'}
            </button>
          </div>

          {/* Route Statistics */}
          {routeData && routeData.point_count > 0 && (
            <div className="mt-6">
              <h4 className="font-semibold text-violet-700 mb-3">Route Statistics</h4>
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                <div className="bg-violet-50 rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold text-violet-600">{routeData.total_distance_miles?.toFixed(1)}</div>
                  <div className="text-xs text-violet-500">Total Miles</div>
                </div>
                <div className="bg-violet-50 rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold text-violet-600">{formatMovementDuration(routeData.total_duration_mins)}</div>
                  <div className="text-xs text-violet-500">Total Time</div>
                </div>
                <div className="bg-blue-50 rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold text-blue-600">{formatMovementDuration(routeData.moving_duration_mins)}</div>
                  <div className="text-xs text-blue-500">Moving Time</div>
                </div>
                <div className="bg-orange-50 rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold text-orange-600">{formatMovementDuration(routeData.stopped_duration_mins)}</div>
                  <div className="text-xs text-orange-500">Stopped Time</div>
                </div>
                <div className="bg-green-50 rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold text-green-600">{routeData.avg_speed_mph?.toFixed(1)}</div>
                  <div className="text-xs text-green-500">Avg Speed (mph)</div>
                </div>
                <div className="bg-red-50 rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold text-red-600">{routeData.max_speed_mph?.toFixed(1)}</div>
                  <div className="text-xs text-red-500">Max Speed (mph)</div>
                </div>
              </div>
              <div className="mt-3 flex gap-4 text-sm text-gray-600">
                <span><strong>{routeData.point_count}</strong> GPS points</span>
                <span><strong>{routeData.stop_count}</strong> stops identified</span>
                <span>From <strong>{formatRouteDateTime(routeData.start_time)}</strong> to <strong>{formatRouteDateTime(routeData.end_time)}</strong></span>
              </div>
            </div>
          )}

          {/* No Data in Time Range - But Data Available */}
          {routeData && routeData.pointCount === 0 && routeData.dataAvailable && routeData.dataRange && (
            <div className="mt-6 bg-amber-50 border border-amber-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <span className="text-2xl">{'\u26A0'}</span>
                <div className="flex-1">
                  <h4 className="font-semibold text-amber-800 mb-2">No Data in Selected Time Range</h4>
                  <p className="text-amber-700 text-sm mb-3">{routeData.message}</p>
                  <div className="bg-white rounded p-3 mb-3">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                      <div>
                        <span className="text-gray-500">Earliest Data:</span>
                        <div className="font-medium">{formatRouteDateTime(routeData.dataRange.earliest)}</div>
                      </div>
                      <div>
                        <span className="text-gray-500">Latest Data:</span>
                        <div className="font-medium">{formatRouteDateTime(routeData.dataRange.latest)}</div>
                      </div>
                      <div>
                        <span className="text-gray-500">Total Points:</span>
                        <div className="font-medium">{routeData.dataRange.totalPoints}</div>
                      </div>
                      <div>
                        <span className="text-gray-500">Hours Since Latest:</span>
                        <div className="font-medium">{Math.round(routeData.dataRange.hoursSinceLatest)} hours</div>
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      setRouteHours(String(routeData.dataRange.suggestedHours));
                      setTimeout(() => handleGetRouteHistory(), 100);
                    }}
                    className="px-4 py-2 bg-amber-600 text-white rounded hover:bg-amber-700"
                  >
                    {'\uD83D\uDD04'} Use Suggested Hours ({routeData.dataRange.suggestedHours}h)
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* No Data At All */}
          {routeData && routeData.pointCount === 0 && !routeData.dataAvailable && (
            <div className="mt-6 bg-gray-50 border border-gray-200 rounded-lg p-4 text-center">
              <span className="text-4xl">{'\uD83D\uDCCD'}</span>
              <h4 className="font-semibold text-gray-700 mt-2">No GPS Data Found</h4>
              <p className="text-gray-500 text-sm mt-1">No location data exists for this person.</p>
            </div>
          )}
        </div>

        {/* Playback Controls */}
        {routeData && routeData.points && routeData.points.length > 0 && (
          <div className="bg-white rounded-lg shadow p-4 mt-4">
            <h4 className="font-semibold text-violet-700 mb-3">Playback Controls</h4>
            <div className="space-y-4">
              {/* Progress Bar */}
              <div className="flex items-center gap-4">
                <span className="text-sm text-gray-600 w-24">
                  {formatRouteTime(routeData.points[Math.floor(playbackProgress * (routeData.points.length - 1))]?.timestamp)}
                </span>
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.001"
                  value={playbackProgress}
                  onChange={handleProgressChange}
                  className="flex-1 h-2 bg-violet-200 rounded-lg appearance-none cursor-pointer"
                />
                <span className="text-sm text-gray-600 w-24 text-right">
                  {formatRouteTime(routeData.points[routeData.points.length - 1]?.timestamp)}
                </span>
              </div>

              {/* Control Buttons */}
              <div className="flex items-center gap-4">
                <button
                  onClick={() => setPlaybackProgress(0)}
                  className="p-2 bg-gray-100 rounded-md hover:bg-gray-200"
                  title="Reset to Start"
                >
                  {'\u23EE'}
                </button>
                <button
                  onClick={handlePlayPause}
                  className={`px-4 py-2 rounded-md font-semibold ${
                    isPlaying
                      ? 'bg-red-500 text-white hover:bg-red-600'
                      : 'bg-violet-600 text-white hover:bg-violet-700'
                  }`}
                >
                  {isPlaying ? '\u23F8 Pause' : '\u25B6 Play'}
                </button>
                <button
                  onClick={() => setPlaybackProgress(1)}
                  className="p-2 bg-gray-100 rounded-md hover:bg-gray-200"
                  title="Skip to End"
                >
                  {'\u23ED'}
                </button>

                {/* Speed Control */}
                <div className="flex items-center gap-2 ml-4">
                  <span className="text-sm text-gray-600">Speed:</span>
                  {[0.5, 1, 2, 4].map(speed => (
                    <button
                      key={speed}
                      onClick={() => handlePlaybackSpeedChange(speed)}
                      className={`px-2 py-1 text-sm rounded ${
                        playbackSpeed === speed
                          ? 'bg-violet-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {speed}x
                    </button>
                  ))}
                </div>
              </div>

              {/* Current Position Info */}
              {(() => {
                const currentPos = interpolatePosition(routeData.points, playbackProgress);
                const currentIdx = Math.floor(playbackProgress * (routeData.points.length - 1));
                const currentPoint = routeData.points[currentIdx];
                const heading = getHeadingDisplay(currentPoint?.heading_degrees);
                return (
                  <div className="flex items-center gap-6 p-3 bg-violet-50 rounded-lg">
                    <div className="text-sm">
                      <span className="text-violet-600 font-medium">Position:</span>{' '}
                      <AddressDisplay lat={currentPos.latitude} lon={currentPos.longitude} compact />
                    </div>
                    {currentPoint?.speed_mph != null && (
                      <div className="text-sm">
                        <span className="text-violet-600 font-medium">Speed:</span>{' '}
                        <span style={{ color: getRouteSegmentColor(currentPoint.speed_mph) }}>
                          {currentPoint.speed_mph.toFixed(1)} mph
                        </span>
                      </div>
                    )}
                    {heading.direction !== 'N/A' && (
                      <div className="text-sm">
                        <span className="text-violet-600 font-medium">Heading:</span>{' '}
                        <span className="text-lg">{heading.arrow}</span> {heading.direction}
                      </div>
                    )}
                    {currentPoint?.is_stop && (
                      <div className="text-sm text-orange-600 font-medium">
                        {'\u23F8'} Stop ({currentPoint.stop_duration_mins} min)
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
          </div>
        )}

        {/* Route Map */}
        {routeData && routeData.points && routeData.points.length > 0 && (
          <div className="bg-white rounded-lg shadow p-4 mt-4">
            <h4 className="font-semibold text-violet-700 mb-3">Route Map</h4>
            <div className="h-96 rounded-lg overflow-hidden border border-gray-300">
              <MapContainer
                center={[routeData.points[0].latitude, routeData.points[0].longitude]}
                zoom={12}
                style={{ height: '100%', width: '100%' }}
                key={`route-map-${routeData.person_id}-${routeData.start_time}`}
              >
                <TileLayer
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                />

                {/* Route line segments with speed-based colors */}
                {routeData.points.slice(0, -1).map((point, idx) => {
                  const nextPoint = routeData.points[idx + 1];
                  const color = getRouteSegmentColor(nextPoint?.speed_mph);
                  const isPastPlayback = idx < Math.floor(playbackProgress * (routeData.points.length - 1));
                  return (
                    <Circle
                      key={`segment-${idx}`}
                      center={[point.latitude, point.longitude]}
                      radius={isPastPlayback ? 15 : 8}
                      pathOptions={{
                        fillColor: isPastPlayback ? color : '#D1D5DB',
                        fillOpacity: isPastPlayback ? 0.8 : 0.3,
                        color: isPastPlayback ? color : '#9CA3AF',
                        weight: 1,
                      }}
                    />
                  );
                })}

                {/* Start marker */}
                <CircleMarker
                  center={[routeData.points[0].latitude, routeData.points[0].longitude]}
                  radius={12}
                  pathOptions={{ fillColor: '#22C55E', fillOpacity: 1, color: '#166534', weight: 2 }}
                >
                  <Popup>
                    <strong>Start</strong><br />
                    {formatRouteDateTime(routeData.start_time)}
                  </Popup>
                </CircleMarker>

                {/* End marker */}
                <CircleMarker
                  center={[routeData.points[routeData.points.length - 1].latitude, routeData.points[routeData.points.length - 1].longitude]}
                  radius={12}
                  pathOptions={{ fillColor: '#EF4444', fillOpacity: 1, color: '#991B1B', weight: 2 }}
                >
                  <Popup>
                    <strong>End</strong><br />
                    {formatRouteDateTime(routeData.end_time)}
                  </Popup>
                </CircleMarker>

                {/* Stop markers */}
                {routeData.stops && routeData.stops.map((stop, idx) => {
                  const config = getStopConfig(stop.duration_mins);
                  return (
                    <CircleMarker
                      key={`stop-${stop.stop_id}`}
                      center={[stop.latitude, stop.longitude]}
                      radius={10}
                      pathOptions={{ fillColor: config.color, fillOpacity: 0.9, color: '#1F2937', weight: 2 }}
                    >
                      <Popup>
                        <strong>{config.icon} {stop.stop_id}</strong><br />
                        <strong>{config.label}</strong><br />
                        <AddressFromMap lat={stop.latitude} lon={stop.longitude} addressMap={stopAddressMap} className="text-xs" /><br />
                        Duration: {formatMovementDuration(stop.duration_mins)}<br />
                        Arrival: {formatRouteTime(stop.arrival_time)}<br />
                        Departure: {stop.departure_time ? formatRouteTime(stop.departure_time) : 'N/A'}
                      </Popup>
                    </CircleMarker>
                  );
                })}

                {/* Current playback position */}
                {(() => {
                  const currentPos = interpolatePosition(routeData.points, playbackProgress);
                  const heading = getHeadingDisplay(currentPos.heading);
                  return (
                    <CircleMarker
                      center={[currentPos.latitude, currentPos.longitude]}
                      radius={14}
                      pathOptions={{ fillColor: '#8B5CF6', fillOpacity: 1, color: '#5B21B6', weight: 3 }}
                    >
                      <Popup>
                        <strong>Current Position</strong><br />
                        {heading.arrow} {heading.direction}<br />
                        Progress: {(playbackProgress * 100).toFixed(1)}%
                      </Popup>
                    </CircleMarker>
                  );
                })()}
              </MapContainer>
            </div>
          </div>
        )}

        {/* Stops List */}
        {routeData && routeData.stops && routeData.stops.length > 0 && (
          <div className="bg-white rounded-lg shadow p-4 mt-4">
            <h4 className="font-semibold text-violet-700 mb-3">Identified Stops ({routeData.stops.length})</h4>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-violet-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-violet-700 uppercase">Stop</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-violet-700 uppercase">Location</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-violet-700 uppercase">Arrival</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-violet-700 uppercase">Departure</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-violet-700 uppercase">Duration</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-violet-700 uppercase">Points</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {routeData.stops.map((stop) => {
                    const config = getStopConfig(stop.duration_mins);
                    return (
                      <tr key={stop.stop_id} className="hover:bg-gray-50">
                        <td className="px-4 py-2 whitespace-nowrap">
                          <span style={{ color: config.color }}>{config.icon}</span> {stop.stop_id}
                        </td>
                        <td className="px-4 py-2 text-sm text-gray-600 max-w-xs">
                          <AddressFromMap lat={stop.latitude} lon={stop.longitude} addressMap={stopAddressMap} />
                        </td>
                        <td className="px-4 py-2 whitespace-nowrap text-sm">{formatRouteDateTime(stop.arrival_time)}</td>
                        <td className="px-4 py-2 whitespace-nowrap text-sm">
                          {stop.departure_time ? formatRouteDateTime(stop.departure_time) : '-'}
                        </td>
                        <td className="px-4 py-2 whitespace-nowrap">
                          <span className="px-2 py-1 rounded text-sm" style={{ backgroundColor: `${config.color}20`, color: config.color }}>
                            {formatMovementDuration(stop.duration_mins)}
                          </span>
                        </td>
                        <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-600">{stop.point_count}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Route Reconstruction Reference */}
        <details className="mt-4">
          <summary className="cursor-pointer text-sm text-violet-700 font-medium hover:text-violet-900">
            Route Reconstruction Reference
          </summary>
          <div className="bg-violet-50 rounded-lg p-4 border border-violet-200 mt-2">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <h5 className="font-medium text-violet-700 mb-2">Speed Colors</h5>
                <ul className="space-y-1 text-gray-700">
                  <li><span className="inline-block w-3 h-3 rounded mr-1" style={{ backgroundColor: '#6B7280' }}></span> <strong>Stationary:</strong> {'<'} 5 mph</li>
                  <li><span className="inline-block w-3 h-3 rounded mr-1" style={{ backgroundColor: '#22C55E' }}></span> <strong>Slow:</strong> 5 - 25 mph</li>
                  <li><span className="inline-block w-3 h-3 rounded mr-1" style={{ backgroundColor: '#3B82F6' }}></span> <strong>Moderate:</strong> 25 - 45 mph</li>
                  <li><span className="inline-block w-3 h-3 rounded mr-1" style={{ backgroundColor: '#F59E0B' }}></span> <strong>Fast:</strong> 45 - 65 mph</li>
                  <li><span className="inline-block w-3 h-3 rounded mr-1" style={{ backgroundColor: '#F97316' }}></span> <strong>Very Fast:</strong> 65 - 85 mph</li>
                  <li><span className="inline-block w-3 h-3 rounded mr-1" style={{ backgroundColor: '#EF4444' }}></span> <strong>Speeding:</strong> {'>'} 85 mph</li>
                </ul>
              </div>
              <div>
                <h5 className="font-medium text-violet-700 mb-2">Stop Types</h5>
                <ul className="space-y-1 text-gray-700">
                  <li><span className="text-lg">{'\u23F8'}</span> <strong>Brief Stop:</strong> {'<'} 15 minutes</li>
                  <li><span className="text-lg">{'\uD83D\uDCCD'}</span> <strong>Short Stop:</strong> 15 - 60 minutes</li>
                  <li><span className="text-lg">{'\uD83C\uDFE0'}</span> <strong>Medium Stop:</strong> 1 - 4 hours</li>
                  <li><span className="text-lg">{'\uD83C\uDFE8'}</span> <strong>Extended Stop:</strong> {'>'} 4 hours</li>
                </ul>
                <h5 className="font-medium text-violet-700 mt-4 mb-2">Map Markers</h5>
                <ul className="space-y-1 text-gray-700">
                  <li><span className="inline-block w-3 h-3 rounded-full bg-green-500 mr-1"></span> <strong>Start Point</strong></li>
                  <li><span className="inline-block w-3 h-3 rounded-full bg-red-500 mr-1"></span> <strong>End Point</strong></li>
                  <li><span className="inline-block w-3 h-3 rounded-full bg-violet-500 mr-1"></span> <strong>Current Playback Position</strong></li>
                </ul>
              </div>
            </div>
          </div>
        </details>
      </section>

      {/* ==================== SECTION 3: ACTIVITY HEATMAPS ==================== */}
      <section id="heatmaps">
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          Activity Heatmaps
          <FeatureInfoButton feature="heatmap" label="Docs" />
        </h3>
        {personId ? (
          <div className="space-y-6">
            {/* Static Heatmap */}
            <div className="bg-white rounded-lg shadow p-4">
              <h3 className="text-lg font-semibold mb-4 text-teal-700">Static Behavioral Heatmap</h3>
              <BehavioralHeatmap alienId={parseInt(personId, 10)} height={550} />
            </div>

            {/* Temporal Heatmap */}
            <div className="bg-white rounded-lg shadow p-4">
              <h3 className="text-lg font-semibold mb-4 text-teal-700">Temporal Heatmap (Animated)</h3>
              <TemporalHeatmap alienId={parseInt(personId, 10)} height={550} />
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow p-6 text-center">
            <h3 className="text-xl font-semibold mb-2">Activity Heatmaps</h3>
            <p className="text-gray-500 mb-4">
              Enter a Person ID above to view their H3 hexagonal behavioral heatmaps,
              showing geographic activity concentration over time.
            </p>
          </div>
        )}

        {/* Heatmap Reference */}
        <details className="mt-4">
          <summary className="cursor-pointer text-sm text-teal-700 font-medium hover:text-teal-900">
            H3 Heatmap Reference
          </summary>
          <div className="bg-teal-50 rounded-lg p-4 border border-teal-200 mt-2">
            <h4 className="font-semibold text-teal-800 mb-2">H3 Resolution Levels</h4>
            <div className="grid grid-cols-4 gap-4 text-sm">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-teal-300 rounded"></div>
                <span>7 (~5km cells)</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-teal-400 rounded"></div>
                <span>8 (~1km cells)</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-teal-500 rounded"></div>
                <span>9 (~350m cells)</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-teal-600 rounded"></div>
                <span>10 (~100m cells)</span>
              </div>
            </div>
            <h4 className="font-semibold text-teal-800 mt-4 mb-2">Cell Metrics</h4>
            <div className="grid grid-cols-2 gap-2 text-sm text-gray-700">
              <div><strong>Activity Count:</strong> Number of GPS points in cell</div>
              <div><strong>Time-Weighted Score:</strong> Recency-adjusted activity</div>
              <div><strong>Visit Count:</strong> Distinct visits to this cell</div>
              <div><strong>Dwell Time:</strong> Total time spent in cell</div>
            </div>
            <h4 className="font-semibold text-teal-800 mt-4 mb-2">Color Scale</h4>
            <div className="flex items-center gap-2 text-sm text-gray-700">
              <div className="w-20 h-4 rounded" style={{ background: 'linear-gradient(to right, #3B82F6, #FBBF24, #EF4444)' }}></div>
              <span>Low activity {'\u2192'} Medium {'\u2192'} High activity (hotspots)</span>
            </div>
          </div>
        </details>
      </section>

      {/* ==================== SECTION 4: BEHAVIOR PROFILE ==================== */}
      <section id="behavior-profile">
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          Behavior Profile
          <FeatureInfoButton feature="clustering" label="Docs" />
        </h3>
        {personId ? (
          <PatternClusteringPanel
            alienId={parseInt(personId, 10)}
            showVisualization={true}
            showFeatures={true}
            compact={false}
          />
        ) : (
          <div className="bg-white rounded-lg shadow p-8 text-center">
            <div className="text-6xl mb-4">&#x1F4CA;</div>
            <h3 className="text-xl font-semibold mb-2">Behavior Profile</h3>
            <p className="text-gray-500 mb-4">
              Enter a Person ID above to view their behavioral cluster assignment,
              feature breakdown, and visualization of similar subjects.
            </p>
          </div>
        )}

        {/* Pattern Clustering Reference */}
        <details className="mt-4">
          <summary className="cursor-pointer text-sm text-indigo-700 font-medium hover:text-indigo-900">
            Cluster Types Reference
          </summary>
          <div className="bg-indigo-50 rounded-lg p-4 border border-indigo-200 mt-2">
            <h4 className="font-semibold text-indigo-800 mb-2">Cluster Types</h4>
            <div className="grid grid-cols-5 gap-4 text-sm">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-green-500 rounded"></div>
                <span>Routine Stable</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-blue-500 rounded"></div>
                <span>Mobile Regular</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-yellow-500 rounded"></div>
                <span>Irregular Patterns</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-purple-500 rounded"></div>
                <span>Night Active</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-red-500 rounded"></div>
                <span>High-Risk Exposure</span>
              </div>
            </div>
            <h4 className="font-semibold text-indigo-800 mt-4 mb-2">Behavioral Features (8)</h4>
            <div className="grid grid-cols-2 gap-2 text-sm text-gray-700">
              <div><strong>Avg Daily Distance:</strong> Miles traveled per day</div>
              <div><strong>Unique Locations/Week:</strong> Location diversity</div>
              <div><strong>Routine Adherence:</strong> Consistency with patterns</div>
              <div><strong>Night Activity Ratio:</strong> 10pm-6am activity</div>
              <div><strong>Weekend Activity Ratio:</strong> Sat/Sun activity</div>
              <div><strong>High-Risk Zone Affinity:</strong> Risk zone visits</div>
              <div><strong>Velocity Variance:</strong> Movement speed variation</div>
              <div><strong>Dwell Time Entropy:</strong> Time distribution</div>
            </div>
            <h4 className="font-semibold text-indigo-800 mt-4 mb-2">Quality Metrics</h4>
            <div className="grid grid-cols-2 gap-2 text-sm text-gray-700">
              <div><strong>Silhouette Score:</strong> -1 to 1 (higher = better cluster fit)</div>
              <div><strong>Distance to Centroid:</strong> Lower = stronger match</div>
            </div>
          </div>
        </details>
      </section>

    </div>
  );
};

export default PolActivityTab;
